// ============================================================================
// CLIENT TYPE SERVICE
// ============================================================================
// Business logic and HTTP calls for the ClientType master.
//
// Backend wraps every response in ApiResponse<T> with this shape:
//   { success, message, data, errors, correlationId, timestamp }
//
// List endpoints additionally wrap data in PagedResult<T>:
//   data: { items, page, pageSize, totalCount, totalPages, hasPrevious, hasNext }
//
// The frontend grids (SyutGrid) consume PaginatedResponse<T> with shape:
//   { success, message, data: T[], pagination: { currentPage, pageSize, totalPages, totalItems, hasNextPage, hasPreviousPage } }
//
// `getAll()` MUST do this transformation — see CLAUDE.md "Service layer transformation".

import { get, post, put, del } from '../client/axiosClient';
import { clientTypeEndpoints } from '../endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
  ClientType,
  CreateClientTypeRequest,
  UpdateClientTypeRequest,
  ClientTypeSearchRequest,
} from '../types';

class ClientTypeService {
  /**
   * Get paginated list with optional filters.
   * Transforms backend PagedResult<T> → frontend PaginatedResponse<T>.
   */
  async getAll(
    params?: ClientTypeSearchRequest
  ): Promise<PaginatedResponse<ClientType>> {
    // Use raw BASE_PATH (NOT clientTypeEndpoints.list()) — buildEndpointWithQuery
    // adds the /v1 prefix internally. Passing a pre-prefixed endpoint causes /v1/v1/...
    const endpoint = params
      ? buildEndpointWithQuery(clientTypeEndpoints.basePath(), params as Record<string, any>)
      : clientTypeEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data; // ApiResponse<PagedResult<ClientType>>
    const pagedResult = raw?.data ?? {};

    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<ClientType>> {
    const response = await get<ApiResponse<ClientType>>(
      clientTypeEndpoints.getById(id)
    );
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<ClientType>> {
    const response = await get<ApiResponse<ClientType>>(
      clientTypeEndpoints.getByCode(code)
    );
    return response.data;
  }

  async create(
    data: CreateClientTypeRequest
  ): Promise<ApiResponse<ClientType>> {
    const response = await post<ApiResponse<ClientType>>(
      clientTypeEndpoints.create(),
      data
    );
    return response.data;
  }

  async update(
    id: ID,
    data: UpdateClientTypeRequest
  ): Promise<ApiResponse<ClientType>> {
    const response = await put<ApiResponse<ClientType>>(
      clientTypeEndpoints.update(id),
      data
    );
    return response.data;
  }

  /**
   * DELETE returns 204 No Content (no response body) on success.
   * Returns 404 if the record doesn't exist.
   */
  async delete(id: ID): Promise<EmptyResponse> {
    await del<void>(clientTypeEndpoints.delete(id));
    return { success: true };
  }
}

export const clientTypeService = new ClientTypeService();
export default clientTypeService;
